import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useTranslation } from "i18n-fe";
import { syncEngine } from "../local/sync";
import { rebuildDatabase } from "../local/db";
import { flushAndRebuildLocalData } from "../local/rebuild";
import { sendStationSnapshotConfirmed } from "../hooks/useStationSnapshotUpload";

// #1902 support-only recovery control. #3568: opened from the gear menu on the
// Admin Options screen (AdminOptions.jsx), which is itself only reachable after
// the show PIN, so this dialog needs no gate of its own and no second PIN.
const OUTCOME_COPY = {
  unsynced: {
    title: "rebuild.blockedTitle",
    body: "rebuild.blockedBody",
    testId: "rebuild-blocked-message",
  },
  localDrafts: {
    title: "rebuild.localDraftsTitle",
    body: "rebuild.localDraftsBody",
    testId: "rebuild-local-drafts-message",
  },
  offline: {
    title: "rebuild.offlineTitle",
    body: "rebuild.offlineBody",
    testId: "rebuild-offline-message",
  },
  hydrateFailed: {
    title: "rebuild.hydrateFailedTitle",
    body: "rebuild.hydrateFailedBody",
    testId: "rebuild-hydrate-failed-message",
  },
  snapshotFailed: {
    title: "rebuild.snapshotFailedTitle",
    body: "rebuild.snapshotFailedBody",
    testId: "rebuild-snapshot-failed-message",
  },
  failed: {
    title: "rebuild.failedTitle",
    body: "rebuild.failedBody",
    testId: "rebuild-failed-message",
  },
};

export default function RebuildLocalDataDialog({ open, onClose, onFinished } = {}) {
  // #1902 — the rebuild.* strings live in locales/en/score.json (the
  // "score" namespace, same as InitialSetup's own useTranslation("score")).
  // useTranslation() with no namespace argument falls back to the DEFAULT
  // namespace and silently fails to resolve these keys, rendering the raw
  // key names ("rebuild.dialogTitle" etc.) instead of the actual copy —
  // caught by a real-Chrome screenshot. The vitest/jsdom unit test could
  // not catch it: its i18n-fe mock resolves keys against score.json
  // directly and DISCARDS the namespace argument, so it stayed green
  // while every string on the confirmation dialog was broken.
  const { t } = useTranslation("score");
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  // #1902 AC3 — set only when a confirmed run came back with ops still
  // queued; the dialog switches to the "still has unsynced work" message
  // and nothing is deleted.
  // #2160: which non-success outcome the dialog is showing: "unsynced"
  // (#1902), "offline" (nothing deleted), "hydrateFailed" (wiped, not
  // refilled) or "failed" (the call itself threw). null = confirm view.
  const [outcome, setOutcome] = useState(null);
  // Card #3255 -- of the unsynced ops that blocked a rebuild, how many sit
  // behind another login this session may not claim (the dialog explains).
  const [heldCount, setHeldCount] = useState(0);
  // Card #3257 -- how many op-less local sheets blocked the rebuild.
  const [draftCount, setDraftCount] = useState(0);

  // A fresh open always starts on the confirm view.
  useEffect(() => {
    if (open) setOutcome(null);
  }, [open]);

  const handleClose = () => {
    if (busy) return;
    onClose?.();
    setOutcome(null);
  };

  // Card #3264 -- `anyway` is the "Rebuild anyway" step shown on the
  // localDrafts refusal: it uploads the station snapshot first and only wipes
  // once the office confirms receiving it. Every other refusal is unchanged.
  const runRebuild = async ({ anyway = false } = {}) => {
    setBusy(true);
    try {
      const result = await flushAndRebuildLocalData({
        syncEngine,
        rebuildDb: rebuildDatabase,
        ...(anyway ? { sendSnapshotFirst: sendStationSnapshotConfirmed } : {}),
      });
      if (result.ok) {
        onClose?.();
        onFinished?.();
        // #628-style pattern: land the scribe back on a usable screen, same
        // route the lock's own onUnlock sends them to, since a rebuild
        // sends them back through show selection anyway (card notes).
        navigate("/setup");
      } else {
        setHeldCount(result.held ?? 0);
        setDraftCount(result.reason === "localDrafts" ? (result.remaining ?? 0) : 0);
        setOutcome(OUTCOME_COPY[result.reason] ? result.reason : "unsynced");
      }
    } catch {
      setOutcome("failed");
    } finally {
      setBusy(false);
    }
  };

  const handleConfirm = () => runRebuild();
  const handleRebuildAnyway = () => runRebuild({ anyway: true });

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        data-testid="rebuild-local-data-dialog"
      >
        {outcome ? (
          <>
            <DialogTitle>{t(OUTCOME_COPY[outcome].title)}</DialogTitle>
            <DialogContent>
              <DialogContentText
                data-testid={OUTCOME_COPY[outcome].testId}
                role="alert"
              >
                {outcome === "unsynced" && heldCount > 0
                  ? t("rebuild.blockedHeldBody", { count: heldCount })
                  : outcome === "localDrafts"
                    ? t("rebuild.localDraftsBody", { count: draftCount })
                    : t(OUTCOME_COPY[outcome].body)}
              </DialogContentText>
            </DialogContent>
            <DialogActions>
              <Button color="inherit" onClick={handleClose} disabled={busy} autoFocus>
                {t("rebuild.close")}
              </Button>
              {(outcome === "localDrafts" || outcome === "snapshotFailed") && (
                <Button
                  color="error"
                  onClick={handleRebuildAnyway}
                  disabled={busy}
                  data-testid="rebuild-anyway-button"
                >
                  {busy ? t("rebuild.working") : t("rebuild.anyway")}
                </Button>
              )}
            </DialogActions>
          </>
        ) : (
          <>
            <DialogTitle>{t("rebuild.dialogTitle")}</DialogTitle>
            <DialogContent>
              <DialogContentText>{t("rebuild.dialogBody")}</DialogContentText>
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="error" fontWeight={600}>
                  {t("rebuild.dialogWarning")}
                </Typography>
              </Box>
            </DialogContent>
            <DialogActions>
              <Button color="inherit" onClick={handleClose} disabled={busy}>
                {t("rebuild.cancel")}
              </Button>
              <Button
                color="error"
                onClick={handleConfirm}
                disabled={busy}
                data-testid="rebuild-confirm-button"
              >
                {busy ? t("rebuild.working") : t("rebuild.confirm")}
              </Button>
            </DialogActions>
          </>
        )}
      </Dialog>
    </>
  );
}
